import { useEffect, useState, type ReactNode } from 'react';
import { Check, Circle, Loader2, Minus, X } from 'lucide-react';

/**
 * How an operation is getting on (ADR-0053): its steps with the current one
 * standing out, a bar for how much of it is done, and a line that says the
 * figures are live and how fresh they are. Every operation that takes longer
 * than a click shows at least one of these, updated while it runs.
 */

export type StepState = 'pending' | 'running' | 'done' | 'failed' | 'skipped';

export interface StepItem {
  readonly label: string;
  readonly state: StepState;
  /** A line under the label: an attempt count, a time, what is waited for. */
  readonly detail?: ReactNode;
}

const ICON: Record<StepState, ReactNode> = {
  done: <Check size={16} aria-hidden="true" />,
  running: <Loader2 size={16} aria-hidden="true" className="spin" />,
  failed: <X size={16} aria-hidden="true" />,
  skipped: <Minus size={14} aria-hidden="true" />,
  pending: <Circle size={10} aria-hidden="true" />,
};

const SAID: Record<StepState, string> = { done: 'готово', running: 'выполняется', failed: 'ошибка', skipped: 'пропущено', pending: 'ожидает' };

/** An operation as a checklist, the running or failed step marked as the current one. */
export function Steps({ items, label }: { readonly items: readonly StepItem[]; readonly label: string }): ReactNode {
  const current = items.findIndex((item) => item.state === 'running' || item.state === 'failed');
  return (
    <ol className="setup-steps" aria-label={label}>
      {items.map((item, index) => (
        <li key={item.label} className={`setup-step setup-step-${item.state}${index === current ? ' setup-step-current' : ''}`} aria-current={index === current ? 'step' : undefined}>
          <span className="setup-step-icon">{ICON[item.state]}</span>
          <span className="setup-step-text">
            <span className="setup-step-label">{item.label}</span>
            {item.detail ? <span className="setup-step-detail">{item.detail}</span> : null}
          </span>
          <span className="visually-hidden">{SAID[item.state]}</span>
        </li>
      ))}
    </ol>
  );
}

/** How much of something is done: of a batch, of a budget, of a quota. */
export function ProgressBar({
  value,
  max = 100,
  label,
  tone = 'accent',
  indeterminate = false,
}: {
  readonly value: number;
  readonly max?: number;
  /** What the bar measures, for screen readers. */
  readonly label: string;
  readonly tone?: 'accent' | 'healthy' | 'degraded' | 'down';
  /** Work under way whose share is not known: the bar moves without a figure. */
  readonly indeterminate?: boolean;
}): ReactNode {
  const share = max > 0 ? Math.min(100, Math.max(0, (value / max) * 100)) : 0;
  return (
    <div
      className={`progress progress-${tone}${indeterminate ? ' progress-indeterminate' : ''}`}
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={max}
      {...(indeterminate ? {} : { 'aria-valuenow': Math.round(value) })}
    >
      <span className="progress-fill" style={indeterminate ? undefined : { width: `${share}%` }} />
    </div>
  );
}

/** Seconds since `at`, kept current: the page re-renders every second while it shows. */
function useSecondsSince(at: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(timer);
  }, []);
  return Math.max(0, Math.round((now - at) / 1_000));
}

function ago(seconds: number): string {
  if (seconds < 5) return 'только что';
  if (seconds < 60) return `${seconds} с назад`;
  const minutes = Math.round(seconds / 60);
  return minutes < 60 ? `${minutes} мин назад` : `${Math.round(minutes / 60)} ч назад`;
}

/**
 * «Обновляется · 3 с назад»: that the page follows the operation by itself,
 * and when it last heard. A failed look says so, and that it keeps trying.
 */
export function LiveStatus({ updatedAt, live, failed = false }: { readonly updatedAt: number; readonly live: boolean; readonly failed?: boolean }): ReactNode {
  const seconds = useSecondsSince(updatedAt);
  if (failed) {
    return (
      <span className="live live-failed" role="status">
        <span className="live-dot" aria-hidden="true" />
        Нет ответа, пробуем снова · данные {ago(seconds)}
      </span>
    );
  }
  return (
    <span className={`live${live ? ' live-on' : ''}`} role="status">
      <span className="live-dot" aria-hidden="true" />
      {live ? 'Обновляется' : 'Обновлено'} · {ago(seconds)}
    </span>
  );
}

/** How long something has been going: «2 мин 10 с». */
export function Elapsed({ since }: { readonly since: string }): ReactNode {
  const seconds = useSecondsSince(new Date(since).getTime());
  const minutes = Math.floor(seconds / 60);
  return <span className="numeric">{minutes > 0 ? `${minutes} мин ${seconds % 60} с` : `${seconds} с`}</span>;
}
